import React, {useEffect, useMemo} from "react";
import {Col, Label, Modal, ModalBody, ModalHeader, Row} from "reactstrap";
import {useDispatch, useSelector} from "react-redux";
import * as yup from "yup";
import {useFormik} from "formik";
import LabelInput from "../../../components/Common/LabelInput";
import {addUser, clearUser, modalUser, updUser} from "../../../store/user/actions";
import Select from "../../../components/Common/Select";
import {useMutation, useQuery} from "@tanstack/react-query";
import {UserService} from "../../../services/user.service";
import {IOffice} from "../../../store/offices/types";
import {clearOffices, getOffices} from "../../../store/offices/actions";
import {get} from "../../../helpers/api_helper";

interface Props {
    isOpen: boolean;
    //toggle: () => void;
}

interface IFormValues {
    name: string;
    email: string;
    buyer_ai: string;
    team_id: number;
    password: string;
    total: string;
    parent_id: number,
    role_ids: any[],
    tg_id: any;
    office_ids: number[];
}

const validationSchema = yup.object().shape({
    name: yup.string()
        .min(1, "Name must contain at least 1 character")
        .max(75, "Name must not exceed 75 characters")
        .matches(
            /^[a-zA-Z 0-9_-]+$/,
            "Name can contain only Latin letters, numbers, spaces, underscores, and hyphens"
        )
        .required("Name is required"),

    team_id: yup.number().required("Team is required"),
    password: yup.string().nullable(),
    total: yup.number().nullable(),
    parent_id: yup.number().nullable(),
    roles: yup.number().nullable(),
    buyer_ai: yup.number()
        .typeError("Buyer AI must be a number")
        .required("Buyer AI is required"),

    tg_id: yup.number().nullable(),
    office_ids: yup.number().nullable(),
});

const ModalTeam = ({isOpen}: Props) => {
    const dispatch = useDispatch();

    const offices = useSelector((state: any) => (state.Offices.data || []) as Array<IOffice>);
    const officesLoading = useSelector((state: any) => (state.Offices.loading || false) as boolean);

    const item = useSelector((state: any) => state.Users.item || {});
    const roles = useSelector((state: any) => state.roles.data);

    useEffect(() => {
        dispatch(getOffices('', {}))

        return () => {
            dispatch(clearOffices())
        };
    }, []);

    const {data: users, isFetching: isLoadingUsers} = useQuery({
        queryKey: ["keyUsers"],
        queryFn: () => get("/users"),
        select: (response) => response.data
    });

    const {data: teams, isFetching: isLoadingTeams} = useQuery({
        queryKey: ["keyTeams"],
        queryFn: () => get("/teams", {params: {per_page: 1000}}),
        select: (response) => response.data
    });

    const {data: tgAccounts, isFetching: isLoadingTgs} = useQuery({
        queryKey: ["keyTgAccounts"],
        queryFn: () => UserService.getTgAccounts()
    });

    const {mutate: getUserPassword, data: userPassword} = useMutation({
        mutationFn: (data: any) => UserService.getUserPassword(data),
        mutationKey: ["getUserPassword"],
        onError: (error: any) => console.error(error),
        onSuccess: (data) => {
            setFieldValue("password", data.password);
        },
    });

    const rolData = useMemo(() => (
        roles.map((item: any) => ({value: item.id, label: item.name}))
    ), [roles]);

    const officesData = useMemo(() => (
        offices.map((item: any) => ({value: item.id, label: item.name}))
    ), [offices]);

    const initialValues = useMemo((): IFormValues => {
        return {
            name: item?.name || "",
            email: item?.email || "",
            buyer_ai: item?.buyer_ai || "",
            team_id: item?.team_id || "",
            password: item?.password || "",
            total: item?.total || 0,
            parent_id: item?.parent_id || null,
            role_ids: item.roles?.map((item: any) => item.id) || "",
            tg_id: item?.tg_id || 0,
            office_ids: item.offices?.map((item: any) => item.id) || ""
        }
    }, [item]);

    const handleSubmitForm = (formValues: IFormValues) => {
        if (item.id) {
            dispatch(updUser(formValues, item.id));
        } else {
            dispatch(addUser(formValues));
        }
    };

    const {
        values,
        handleChange,
        handleSubmit,
        handleBlur,
        errors,
        touched,
        isValid,
        resetForm,
        setFieldValue
    } = useFormik({
        initialValues: initialValues,
        onSubmit: handleSubmitForm,
        validationSchema: validationSchema,
        validateOnBlur: true,
        validateOnChange: true,
        validateOnMount: true,
        enableReinitialize: true
    });

    const teamsData = useMemo(() => (
        teams?.filter((item: any) => values.office_ids.includes(item.office_id))
            .map((item: any) => ({value: item?.id, label: item?.name}))
    ), [values.office_ids, teams]);

    const filteredUsers = useMemo(() => (
        users?.filter((item: any) => item.office_ids?.some((officeId: any) => values.office_ids.includes(officeId)))
    ), [values.office_ids, users])

    const resUsers = useMemo(() => (
        filteredUsers?.map((item: any) => ({value: item?.id, label: item?.name}))
    ), [filteredUsers]);

    const tgUserId = useMemo(() => (
        tgAccounts?.accounts
            .filter((item: any) => item.office_ids?.some((officeId: any) => values.office_ids.includes(officeId)))
            .map((item: any) => ({value: item.id, label: item.name})) || []
    ), [tgAccounts]);

    const close = () => {
        dispatch(modalUser(false));
        dispatch(clearUser());
        resetForm();
    };

    const handleChangeSelect = (name: string, value: any) => {
        setFieldValue(name, value?.value)
    }

    const handleChangeMulti = (name: string, value: any) => {
        const multiIds = value.map((item: any) => item.value)
        setFieldValue(name, multiIds)
    }

    const handleChangeOffice = (value: any) => {
        const officeIds = value.map((item: any) => item.value)

        if (!teamsData.some((item: any) => item.value === values?.team_id))
            setFieldValue("team_id", null)

        if (!resUsers.some((item: any) => item.value === values?.parent_id))
            setFieldValue("parent_id", null)

        if (!tgUserId.some((item: any) => item.value === values?.tg_id))
            setFieldValue("tg_id", null)

        setFieldValue("office_ids", officeIds)
    }

    const handleTogglePassword = (isShown: boolean) => {
        if (isShown && item) getUserPassword(item)
    };

    const isSelectedOffice = values.office_ids.length > 0

    return (
        <Modal isOpen={isOpen} toggle={close}>
            <ModalHeader toggle={close} tag="h4">
                {item?.id ? "Edit" : "Add"} User
            </ModalHeader>
            <ModalBody>
                <form onSubmit={handleSubmit}>
                    <Row>
                        <Col xs={12}>
                            <div className="mb-3">
                                <LabelInput
                                    label="Name*"
                                    placeholder="Name*"
                                    name="name"
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    value={values.name || ""}
                                    hasError={!!(errors.name && touched.name)}
                                    errorText={errors.name}
                                />
                            </div>
                            <div className="mb-3">
                                <LabelInput
                                    label="email*"
                                    placeholder="email*"
                                    name="email"
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    value={values.email || ""}
                                    hasError={!!(errors.email && touched.email)}
                                    errorText={errors.email}
                                />
                            </div>
                            <div className="mb-3">
                                <LabelInput
                                    label="buyer ai*"
                                    placeholder="buyer_ai*"
                                    name="buyer_ai"
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    value={values.buyer_ai || ""}
                                    hasError={!!(errors.buyer_ai && touched.buyer_ai)}
                                    errorText={errors.buyer_ai}
                                />
                            </div>
                            <div className="mb-3">
                                <LabelInput
                                    label="password"
                                    placeholder={item.id ? "password" : "password*"}
                                    name="password"
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    value={values.password}
                                    hasError={!!(errors.password && touched.password)}
                                    errorText={errors.password}
                                    type="password"
                                    onTogglePassword={handleTogglePassword}
                                />
                            </div>
                            <div className="mb-3">
                                <LabelInput
                                    label="total"
                                    placeholder="total"
                                    name="total"
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    value={values.total || ""}
                                    hasError={!!(errors.total && touched.total)}
                                    errorText={errors.total}
                                />
                            </div>
                            {officesData &&
                                <div className='mb-3'>
                                    <Label>Offices:</Label>
                                    <Select
                                        isMulti
                                        isSearchable
                                        label="Offices"
                                        placeholder="Offices"
                                        isLoading={officesLoading}
                                        disabled={officesLoading}
                                        options={officesData}
                                        onChange={handleChangeOffice}
                                        value={!officesLoading && officesData.filter((item: any) => values?.office_ids.includes(item.value))}
                                    />
                                </div>
                            }
                            <div className='mb-3'>
                                <Label>Team: </Label>
                                <Select
                                    isSearchable
                                    name="team_id"
                                    label="Team"
                                    placeholder="Team"
                                    isLoading={isLoadingTeams}
                                    isDisabled={isLoadingTeams || !isSelectedOffice}
                                    options={teamsData}
                                    onChange={(value: any) => handleChangeSelect("team_id", value)}
                                    value={teamsData?.find((item: any) => item.value === values?.team_id) || null}
                                />
                            </div>
                            <div className='mb-3'>
                                <Label>Parent: </Label>
                                <Select
                                    isSearchable
                                    label="Parent"
                                    name="parent_id"
                                    placeholder="Parent"
                                    isLoading={isLoadingUsers}
                                    isDisabled={isLoadingUsers || !isSelectedOffice}
                                    options={resUsers}
                                    onChange={(value: any) => handleChangeSelect("parent_id", value)}
                                    value={resUsers?.find((item: any) => item.value === values?.parent_id) || null}
                                />
                            </div>
                            {rolData &&
                                <div className='mb-3'>
                                    <Label>Roles:</Label>
                                    <Select
                                        isMulti
                                        isSearchable
                                        label="Roles"
                                        placeholder="Roles"
                                        options={rolData}
                                        onChange={(value: any) => handleChangeMulti("role_ids", value)}
                                        defaultValue={rolData.filter((item: any) => values?.role_ids.includes(item.value))}
                                    />
                                </div>
                            }
                            <div className='mb-3'>
                                <Label>Telegram: </Label>
                                <Select
                                    isSearchable
                                    name="Telegram"
                                    label="Telegram"
                                    placeholder="Telegram"
                                    isLoading={isLoadingTgs}
                                    disabled={isLoadingTgs}
                                    options={tgUserId}
                                    onChange={(value: any) => handleChangeSelect("tg_id", value)}
                                    value={!isLoadingTgs && (tgUserId.find((item: any) => item.value === values.tg_id) || null)}
                                />
                            </div>
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <div className="text-end">
                                <button type="submit" className="btn btn-success save-user" disabled={!isValid}>
                                    Save
                                </button>
                            </div>
                        </Col>
                    </Row>
                </form>
            </ModalBody>
        </Modal>
    )
}

export default ModalTeam;